// The user's uploaded manuals: filename, equipment, date, status, chunk
// count, and Delete (with confirm). A document stuck in 'processing' (e.g.
// the browser was closed mid-upload) is flagged incomplete and can be
// deleted to free its plan slot.
import { useState } from 'react';
import { callManualsApi } from './manualsApi';

const STATUS_STYLES = {
  ready: { color: 'var(--green)', background: 'rgba(52,211,153,0.12)', label: 'READY' },
  processing: {
    color: 'var(--yellow)',
    background: 'rgba(251,191,36,0.12)',
    label: 'INCOMPLETE',
  },
  failed: { color: 'var(--red)', background: 'rgba(248,113,113,0.12)', label: 'FAILED' },
};

export default function ManualsList({ documents, onChanged, onChat }) {
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  async function handleDelete(doc) {
    const sure = window.confirm(
      `Delete "${doc.filename}" and all of its indexed content? This cannot be undone.`
    );
    if (!sure) return;
    setDeletingId(doc.id);
    setError(null);
    try {
      await callManualsApi('delete_document', { document_id: doc.id });
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  }

  if (!documents.length) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
        No manuals uploaded yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {error && <div style={{ color: 'var(--red)', fontSize: 12.5 }}>{error}</div>}
      {documents.map(doc => {
        const status = STATUS_STYLES[doc.status] || STATUS_STYLES.processing;
        return (
          <div
            key={doc.id}
            style={{
              backgroundColor: 'var(--surface-1)',
              border: '1px solid var(--border-soft)',
              borderRadius: 12,
              padding: 14,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  wordBreak: 'break-all',
                  marginBottom: 4,
                }}
              >
                {doc.filename}
              </div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  color: 'var(--text-3)',
                  letterSpacing: 0.5,
                }}
              >
                {doc.equipment_path}
                {' · '}
                {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB') : ''}
                {' · '}
                {doc.chunk_count} chunks
              </div>
              {doc.status === 'processing' && (
                <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 4 }}>
                  This upload never finished — delete it and upload the PDF again.
                </div>
              )}
            </div>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: 1,
                borderRadius: 6,
                padding: '4px 10px',
                color: status.color,
                backgroundColor: status.background,
              }}
            >
              {status.label}
            </span>
            {doc.status === 'ready' && onChat && (
              <button
                className="btn"
                onClick={() => onChat(doc)}
                style={{
                  padding: '7px 16px',
                  fontSize: 11,
                  background: 'var(--primary)',
                  color: '#ffffff',
                }}
              >
                💬 Chat
              </button>
            )}
            <button
              className="btn btn-ghost"
              onClick={() => handleDelete(doc)}
              disabled={deletingId === doc.id}
              style={{
                padding: '7px 14px',
                fontSize: 11,
                borderColor: 'rgba(248,113,113,0.5)',
                color: 'var(--red)',
              }}
            >
              {deletingId === doc.id ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
